import IFACE, { EL } from './IFACE.js';
import Activate from '../event/Activate.js';
import Click from '../event/Click.js';
import Deactivate from '../event/Deactivate.js';
import Deselect from '../event/Deselect.js';
import Disable from '../event/Disable.js';
import Enable from '../event/Enable.js';
import LongClick from '../event/LongClick.js';
//import { LongclickDelay } from '../enums/StyleVars.js';
import { makeStruct } from '../enums/MODELS.js';
import Select from '../event/Select.js';
import Selectable from './Selectable.js';
/** Create a Clickable model structure constructor
    @type {function(ClickableOptions): Model}
    @param {ClickableOptions} Model
    @returns {Model} Model
*/
export const clickableOptions = makeStruct([
    ['deactivateSiblings', false],
    ['delay', 200],
    ['longClickDelay', 2000],
    ['stopPropagation', true]
]);
/** An interface for Click driven Events */
export default class Clickable extends IFACE {
    options;
    /** A series of Click Related Events and Methods
        A 'clickable' element is capable of performing 5 separate events
            - Single Click toggles Activation/Deactivation
            - Double Click toggles Selection/Deselection
            - Long Click performs contextual Events, Triggers etc
        @todo Stop Propagation could use some work
        @param {EL} node Class to implement this interface (Typically 'this')
        @param {ClickableOptions} options Click Timer Options
    */
    constructor(node, options = {
        deactivateSiblings: false,
        delay: 200,
        longClickDelay: 2000,
        stopPropagation: true
    }) {
        super(node, 'clickable');
        this.options = options;
        // node.implement(new Switchable(node));
        node.implement(new Selectable(node));
        //node.el.style.webkitTouchCallout = 'none'; // Moved to _event.scss
        // (node.el.style as any)['webkitTouchCallout'] = 'none';
        node.timer = null;
        node.touchtime = 0; // mobile double click detection
    }
    /** Creates a Clickable Options constructor
        @returns {function(): ClickableOptions} ClickableOptions Constructor

    createOptions() {
        return this.node.makeStruct([
            ['deactivateSiblings', false],
            ['delay', 200],
            ['longClickDelay', LongclickDelay],
            ['stopPropagation', true]
        ]);
    }*/
    /** Disables the browser context menu
        @description This is generally not considered a good practice.  Use sparingly and be sure to enable again once you're done
        @see https://stackoverflow.com/questions/737022/how-do-i-disable-right-click-on-my-web-page
        @returns {Promise<boolean>} Resolves true on completion
    */
    disableContextMenu() {
        return new Promise((resolve) => {
            document.addEventListener('contextmenu', this.callPreventDefault);
            resolve(true);
        });
    }
    /** Enables the Context Menu in cases where it may have been disabled
        @param delay Delay
        @returns {Promise<boolean>} Resolves true on completion
    */
    enableContextmenu(delay = 500) {
        return new Promise((resolve) => {
            setTimeout(() => document.removeEventListener('contextmenu', this.callPreventDefault), delay);
            resolve(true);
        });
    }
    /** Calls Event.preventDefault attached to an Event Listener
        @param {Event} ev Event
    */
    callPreventDefault(ev) {
        ev.preventDefault();
    }
    addListeners(node) {
        // Detect Long click on desktop (MouseEvent) and mobile (TouchEvent)
        // @see https://developer.mozilla.org/en-US/docs/Web/API/Touch_events
        // Consider showing a simple press-timer animation after 100ms
        node.on('mousedown', (ev) => node.pressDown(ev), { passive: true });
        node.on('mouseup', (ev) => node.pressUp(ev), { passive: true });
        node.on('touchstart', (ev) => node.pressDown(ev), { passive: true });
        node.on('touchend', (ev) => node.pressUp(ev), { passive: true });
        node.on('click', (ev) => node.pressed(ev), { passive: true });
    }
    startTimer(node) {
        clearTimeout(node.timer);
        node.touchtime = 1;
    }
    resetTimer(node) {
        node.touchtime = 0;
        clearTimeout(node.timer);
    }
    /** On PressDown Event, starts a timer that triggers the Long Press Event
        @param {EL} node Element to implement methods
        @param {Event} ev Event
    */
    pressDown(node, ev) {
        node.addClass('press');
        node.timer = window.setTimeout(() => {
            this.disableContextMenu().then(() => {
                ev.stopPropagation();
                this.startTimer(node);
                Promise.resolve(node.longclick()).then(() => {
                    node.removeClass('press');
                    this.enableContextmenu();
                });
            });
        }, this.options.longClickDelay);
    }
    /** On PressUp Event, cancels PressDown Timer
        @param {EL} node Element to implement methods
        @param {Event} ev Event
    */
    pressUp(node, ev) {
        ev.stopPropagation();
        node.removeClass('press');
        clearTimeout(node.timer);
    }
    setMethods(node) {
        /** Toggle the 'active' state of this Element and dispatch appropriate Event */
        this.methods.click = () => {
            this.toggle('active', new Activate(node), new Deactivate(node));
        };
        /** Toggle the 'selected' state of this Element and dispatch appropriate Event */
        this.methods.dblclick = () => this.toggle('selected', new Select(node), new Deselect(node));
        /** Dispatches a LongClick Event */
        this.methods.longclick = () => node.do(new LongClick(node));
        /** On PressDown Event, starts a timer that triggers the Long Press Event
            @param {Event} ev Event
            @returns {Promise<any>} Promise to resolve given function
        */
        this.methods.pressDown = (ev) => this.pressDown(node, ev);
        /** On PressUp Event, cancels PressDown Timer
            @param {Event} ev Event
        */
        this.methods.pressUp = (ev) => this.pressUp(node, ev);
        /** Press Event occurs after Press Down/Up events complete,
            signalling that the element has been pressed
            @param {Event} ev Event
        */
        this.methods.pressed = (ev) => {
            //console.log('Pressed', this.timer, ev);
            ev.stopPropagation();
            try {
                this.disableContextMenu().then(() => {
                    if (node.touchtime === 0) {
                        node.touchtime = new Date().getTime();
                        setTimeout(() => {
                            if (node.touchtime !== 0) {
                                //console.log('click-single', node.timer, node);
                                node.removeClass('press');
                                this.resetTimer(node);
                                Promise.resolve(node.click()).then(() => this.enableContextmenu());
                            }
                        }, this.options.delay);
                    }
                    else if (new Date().getTime() - node.touchtime < this.options.delay) {
                        //console.log('click-double', node.timer, node);
                        node.removeClass('press');
                        this.resetTimer(node);
                        Promise.resolve(node.dblclick()).then(() => this.enableContextmenu());
                    }
                    else {
                        //console.log('longclick-up', node.timer, node);
                        node.removeClass('press');
                        this.resetTimer(node);
                        Promise.resolve(true).then(() => this.enableContextmenu());
                    }
                });
            }
            catch (e) {
                console.warn('Clickable Error', e);
                node.removeClass('press');
                this.resetTimer(node);
                Promise.reject(e).then(() => this.enableContextmenu());
            }
        };
    }
}
export { Activate, Click, Deactivate, Deselect, Disable, EL, Enable, IFACE, Select, Selectable };
